/** The subset of Wavefront MTL that a textured photogrammetry mesh actually uses. */
export interface MtlMaterial {
  name: string;
  /** Diffuse colour, defaults to white so an untextured tile is still visible. */
  diffuse: [number, number, number];
  /** Texture map file name, relative to the MTL. */
  diffuseMap?: string;
  opacity: number;
  /** `illum 0` means "constant colour, ignore lighting" — common for draped textures. */
  unlit: boolean;
}

export function parseMtl(text: string): Map<string, MtlMaterial> {
  const materials = new Map<string, MtlMaterial>();
  let current: MtlMaterial | null = null;

  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line || line.startsWith("#")) continue;
    const space = line.search(/\s/);
    const keyword = (space < 0 ? line : line.slice(0, space)).toLowerCase();
    const value = space < 0 ? "" : line.slice(space + 1).trim();

    switch (keyword) {
      case "newmtl":
        current = { name: value, diffuse: [1, 1, 1], opacity: 1, unlit: false };
        materials.set(value, current);
        break;
      case "kd": {
        const rgb = value.split(/\s+/).map(Number);
        if (current && rgb.length >= 3 && rgb.every(Number.isFinite)) {
          current.diffuse = [rgb[0], rgb[1], rgb[2]];
        }
        break;
      }
      case "map_kd":
        if (current) current.diffuseMap = stripMapOptions(value);
        break;
      case "d":
        if (current && Number.isFinite(Number(value))) current.opacity = Number(value);
        break;
      case "tr":
        // `Tr` is transparency, the complement of `d`.
        if (current && Number.isFinite(Number(value))) current.opacity = 1 - Number(value);
        break;
      case "illum":
        if (current) current.unlit = Number(value) === 0;
        break;
      default:
        break;
    }
  }
  return materials;
}

/**
 * `map_Kd` may carry options before the file name (`-s 1 1 1 texture.jpg`), and the name
 * itself can contain spaces. Everything after the last option flag is the path.
 */
function stripMapOptions(value: string): string {
  const tokens = value.split(/\s+/);
  let index = 0;
  while (index < tokens.length && tokens[index].startsWith("-")) {
    const flag = tokens[index];
    // -s/-o/-t take three numbers, -mm takes two, the rest take one.
    const argCount = flag === "-s" || flag === "-o" || flag === "-t" ? 3 : flag === "-mm" ? 2 : 1;
    index += 1 + argCount;
  }
  return tokens.slice(index).join(" ") || value;
}
